import React, { useState } from "react";
import { motion } from "@/lib/framer-motion-shim";
import { UtensilsCrossed, Instagram, Phone, MapPin, Clock, Star, Calendar, Wine, Leaf } from "lucide-react";
import { useLocation } from "react-router-dom";
import BookingModal from "@/components/demos/BookingModal";
import ContactForm from "@/components/demos/ContactForm";
import DemoTopBar from "@/components/demos/DemoTopBar";
import Seo from "@/components/Seo";

const images = [
  "https://images.unsplash.com/photo-1768162125972-724be0bbc063?w=800&q=80",
  "https://images.unsplash.com/photo-1726160185608-09cca9162b6c?w=800&q=80",
  "https://images.unsplash.com/photo-1573013919066-ab9778f4d0e1?w=800&q=80",
  "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80",
];

const menuCategories = [
  {
    name: "Entradas",
    items: [
      { name: "Carpaccio de Polvo", description: "Com azeite de ervas e flor de sal", price: "14€" },
      { name: "Queijo da Serra", description: "Acompanhado de doce de abóbora", price: "12€" },
      { name: "Salada de Tomate", description: "Tomate coração de boi, queijo fresco, manjericão", price: "9€" },
    ]
  },
  {
    name: "Pratos Principais",
    items: [
      { name: "Bacalhau à Lagareiro", description: "Com batata a murro e grelos", price: "24€" },
      { name: "Secretos de Porco Preto", description: "Com migas de espargos e ovo", price: "22€" },
      { name: "Arroz de Pato", description: "Tradicional, com laranja e chouriço", price: "19€" },
      { name: "Polvo à Brás", description: "Releitura contemporânea do clássico", price: "26€" },
    ]
  },
  {
    name: "Sobremesas",
    items: [
      { name: "Toucinho do Céu", description: "Com gelado de amêndoa", price: "8€" },
      { name: "Mousse de Chocolate", description: "Chocolate negro 70%, flor de sal", price: "7€" },
    ]
  }
];

export default function RestaurantDemo() {
  const location = useLocation();
  const [bookingOpen, setBookingOpen] = useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("agendar") === "1") {
      setBookingOpen(true);
    }
  }, [location.search]);

  return (
    <>
      <Seo
        title="Demo Restaurante | Web & Studio"
        description="Template demo para restaurante com menu, reservas, galeria e formulário de contacto."
        path="/demo/restaurant"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="min-h-screen bg-stone-100 text-stone-900 theme-restaurant"
      >
        <DemoTopBar />

      {/* Nav */}
      <nav className="relative z-40 bg-stone-100/95 backdrop-blur-sm border-b border-stone-200">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <UtensilsCrossed className="w-6 h-6 text-orange-600" />
              <span className="text-xl font-semibold">Sabores da Terra</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#menu" className="text-stone-600 hover:text-stone-900 transition-colors">Menu</a>
              <a href="#about" className="text-stone-600 hover:text-stone-900 transition-colors">Sobre</a>
              <a href="#contact" className="text-stone-600 hover:text-stone-900 transition-colors">Contacto</a>
            </div>
            <button
              onClick={() => setBookingOpen(true)}
              className="px-6 py-2 bg-orange-600 text-white rounded-full hover:bg-orange-700 transition-colors duration-200"
              data-testid="nav-booking-btn"
            >
              Reservar
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images[0]}
            alt="Restaurant"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-stone-900/50" />
        </div>
        
        <div className="relative z-10 text-center text-white px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-px bg-white/50" />
              <Leaf className="w-6 h-6" />
              <div className="w-12 h-px bg-white/50" />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-4">
              Sabores da Terra
            </h1>
            <p className="text-xl md:text-2xl font-light tracking-wide text-white/80 mb-8">
              Cozinha Portuguesa Contemporânea
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setBookingOpen(true)}
                className="px-8 py-4 bg-orange-600 text-white rounded-full hover:bg-orange-700 transition-colors duration-200"
                data-testid="hero-booking-btn"
              >
                Fazer Reserva
              </button>
              <a
                href="#menu"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full hover:bg-white/20 transition-colors duration-200"
              >
                Ver Menu
              </a>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-1.5 bg-white rounded-full"
            />
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={images[3]}
                alt="Restaurant interior"
                className="w-full aspect-[4/5] object-cover rounded-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-orange-600 text-white p-6 rounded-2xl">
                <div className="flex items-center gap-2 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-sm opacity-90">Melhor Restaurante 2024</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-orange-600 uppercase tracking-[0.2em] text-sm">A Nossa História</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
                Tradição com um Toque Moderno
              </h2>
              <p className="text-stone-600 text-lg leading-relaxed mb-6">
                No Sabores da Terra, celebramos a riqueza da gastronomia portuguesa 
                com um olhar contemporâneo. Cada prato é uma viagem pelos sabores 
                autênticos do nosso país, preparados com ingredientes locais e sazonais.
              </p>
              <p className="text-stone-600 text-lg leading-relaxed mb-8">
                O nosso chef, com mais de 20 anos de experiência, combina técnicas 
                tradicionais com apresentações modernas, criando experiências 
                gastronómicas únicas.
              </p>
              <div className="flex items-center gap-8">
                <div>
                  <p className="text-4xl font-bold text-orange-600">15+</p>
                  <p className="text-stone-500">Anos de Experiência</p>
                </div>
                <div className="w-px h-16 bg-stone-200" />
                <div>
                  <p className="text-4xl font-bold text-orange-600">100%</p>
                  <p className="text-stone-500">Produtos Locais</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Menu */}
      <section id="menu" className="py-24 lg:py-32">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-orange-600 uppercase tracking-[0.2em] text-sm">A Nossa Carta</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4">Menu</h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-16">
            {menuCategories.map((category, catIndex) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: catIndex * 0.1 }}
              >
                <h3 className="text-2xl font-semibold mb-8 flex items-center gap-4">
                  <Wine className="w-5 h-5 text-orange-600" />
                  {category.name}
                </h3>
                <div className="space-y-6">
                  {category.items.map((item, itemIndex) => (
                    <div
                      key={item.name}
                      className="group flex justify-between items-start gap-4 p-4 -mx-4 rounded-xl hover:bg-white transition-colors duration-200"
                    >
                      <div className="flex-1">
                        <h4 className="text-xl font-medium mb-1">{item.name}</h4>
                        <p className="text-stone-500">{item.description}</p>
                      </div>
                      <span className="text-xl font-semibold text-orange-600 whitespace-nowrap">{item.price}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <button
              onClick={() => setBookingOpen(true)}
              className="px-8 py-4 bg-orange-600 text-white rounded-full hover:bg-orange-700 transition-colors duration-200"
              data-testid="menu-booking-btn"
            >
              Reservar Mesa
            </button>
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-orange-600 uppercase tracking-[0.2em] text-sm">Galeria</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4">Os Nossos Pratos</h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {images.map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative group overflow-hidden rounded-xl aspect-square"
              >
                <img
                  src={img}
                  alt={`Food ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-orange-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 lg:py-32">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-orange-600 uppercase tracking-[0.2em] text-sm">Contacto</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-8">Visite-nos</h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-stone-500 text-sm">Morada</p>
                    <p className="text-lg">Praça do Comércio, 78 - Coimbra</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-stone-500 text-sm">Reservas</p>
                    <p className="text-lg">+351 934 567 890</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-stone-500 text-sm">Horário</p>
                    <p className="text-lg">Ter-Dom: 12h-15h, 19h-23h</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center hover:bg-orange-200 transition-colors duration-200">
                  <Instagram className="w-5 h-5 text-orange-600" />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg"
            >
              <h3 className="text-2xl font-semibold mb-6">Envie-nos uma mensagem</h3>
              <ContactForm demoType="restaurant" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-stone-900 text-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <UtensilsCrossed className="w-5 h-5" />
              <span>© 2024 Sabores da Terra</span>
            </div>
            <p className="text-stone-400 text-sm">Demo Template - Restaurante</p>
          </div>
        </div>
      </footer>

        <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} demoType="restaurant" />
      </motion.div>
    </>
  );
}




